import { NonEmptyArray } from '@/utils/typescriptHelpers';
import moment, { Moment } from 'moment-timezone';

import { TimeWindow } from './types';

export const momentFromStr = (str: string): Moment => moment(str);

// Q: Why is this needed? Could a caller not just use the `moment` function directly?
// A: Yes, but Typescript doesn't work in certain situations where you have to pass
//  `moment` directly as a callback to other functions. So this function wraps `moment`.
export const momentFromDate = (date: Date): Moment => moment(date);

export const guessTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;
export const guessTimezoneIfNull = (timezone: string | null): string => timezone ?? guessTimezone();

// Raw `Date` objects in JS are created according to the _user's_ local timezone, starting
//  at 00:00 (midnight). If the user is in a timezone different than the timezone
//  they are scheduling in, this will then become:
//                00:00 +/- (timezone difference)
//  when we convert to `Moment`s that use timezones.
//  This is problematic if, for example, the user is on Eastern time but
//  scheduling in Pacific time, resulting in:
//                06-30 00:00 EST -> 06-29 21:00 PST
//  Thus, we need to adjust the date to account for both the user's timezone
//  as well as the timezone they are scheduling in. We mainly use this function
//  in conjunction with the Mantine `Calendar` component which deals with raw
//  `Date` objects.
export const timezoneAdjustedMoment = (datetime: Moment, timezone: string | null): Moment => {
    const timezoneOffset = Math.max(
        moment(datetime).utcOffset() - moment.tz(timezone ?? guessTimezone()).utcOffset(),
        0,
    );
    const timezoneAdjustedDate = moment(datetime).add(timezoneOffset, 'minutes');
    return timezoneAdjustedDate;
};

// Returns the current time today or tomorrow, rounded to the hour.
//  These are good default values for datetime pickers, etc.
export const startOfCurrentHour = (): Moment => moment().minutes(0).seconds(0).millisecond(0);
export const sometimeTomorrow = (): Moment => startOfCurrentHour().add(1, 'day');
export const sometimeOn = (date: Moment): Moment =>
    date.clone().minutes(0).seconds(0).millisecond(0);
export const startOfDate = (date: Moment): Moment => date.clone().startOf('day');

export const isToday = (date: Moment) => date.isSame(moment(), 'day');
export const isBeforeToday = (date: Moment) => date.isBefore(moment(), 'day');

export const EASTERN_TZSTR = 'America/New_York';
export const CENTRAL_TZSTR = 'America/Chicago';
export const PACIFIC_TZSTR = 'America/Los_Angeles';

export const US_TIMEZONES = [
    {
        name: 'Eastern Time (America/New_York)',
        value: EASTERN_TZSTR,
    },
    {
        name: 'Central Time (America/Chicago)',
        value: CENTRAL_TZSTR,
    },
    {
        name: 'Mountain Time (America/Denver)',
        value: 'America/Denver',
    },
    {
        name: 'Mountain Time (America/Phoenix)',
        value: 'America/Phoenix',
    },
    {
        name: 'Pacific Time (America/Los_Angeles)',
        value: PACIFIC_TZSTR,
    },
    {
        name: 'Alaska Time (America/Anchorage)',
        value: 'America/Anchorage',
    },
    {
        name: 'Hawaii Time (Pacific/Honolulu)',
        value: 'Pacific/Honolulu',
    },
];

export const minDatetime = (datetimes: NonEmptyArray<Moment>): Moment => {
    let min = datetimes[0];
    for (const datetime of datetimes) {
        if (datetime.isBefore(min)) {
            min = datetime;
        }
    }
    return min;
};
export const maxDatetime = (datetimes: NonEmptyArray<Moment>): Moment => {
    let max = datetimes[0];
    for (const datetime of datetimes) {
        if (datetime.isAfter(max)) {
            max = datetime;
        }
    }
    return max;
};

// Returns array at of times the start of the day for each day in the range inclusive.
export const dateRange = ({
    start: rawStart,
    end: rawEnd,
    timezone,
}: {
    start: Moment;
    end: Moment;
    timezone: string;
}): Moment[] => {
    const result = [];
    const start = rawStart.clone().tz(timezone).startOf('day');
    const end = rawEnd.clone().tz(timezone).startOf('day');
    let day = start.clone();
    while (day.isSameOrBefore(end, 'day')) {
        result.push(day.clone());
        day = day.add(1, 'day').tz(timezone).startOf('day');
    }
    return result;
};

export const uniqueDatetimes = (datetimes: Moment[]): Moment[] =>
    // Copied from https://stackoverflow.com/a/53662688/1292652
    Object.values(
        datetimes.reduce<Record<string, Moment>>(
            (datetimesIndexedByStringRepresentation, datetime) => {
                datetimesIndexedByStringRepresentation[datetime.utc().format()] = datetime;
                return datetimesIndexedByStringRepresentation;
            },
            {},
        ),
    );

export const MINIMUM_DATE = moment().year(1900).startOf('year');
export const MAXIMUM_DATE = moment().year(2400).endOf('year');

export const DEFAULT_VISIT_DATE_RANGE_DAYS = 90;
export const VISIT_MAX_DATE_RANGE_DAYS = 365;

export const getDateRangeAroundToday = (days: number) => ({
    startDate: moment().subtract(days, 'days').startOf('day'),
    endDate: moment().add(days, 'days').endOf('day'),
});

// Spans the earliest to latest visit and always includes today. Falls back to a window around
// today when there are no visits, and shrinks to maxDays when the span is wider, keeping today inside.
export const getVisitSpanDateRange = ({
    earliestVisit,
    latestVisit,
    maxDays,
    fallbackDays,
}: {
    earliestVisit: Moment | null;
    latestVisit: Moment | null;
    maxDays: number;
    fallbackDays: number;
}) => {
    if (!earliestVisit || !latestVisit) {
        return getDateRangeAroundToday(fallbackDays);
    }

    const today = moment();
    let startDate = moment.min(earliestVisit, today).clone().startOf('day');
    let endDate = moment.max(latestVisit, today).clone().endOf('day');

    if (endDate.isAfter(startDate.clone().add(maxDays, 'days'), 'day')) {
        startDate = endDate.clone().subtract(maxDays, 'days').startOf('day');
    }
    if (startDate.isAfter(today, 'day')) {
        const earliestStart = moment.min(earliestVisit, today).clone().startOf('day');
        const oldestStart = today.clone().subtract(maxDays, 'days').startOf('day');
        startDate = moment.max(earliestStart, oldestStart);
        endDate = startDate.clone().add(maxDays, 'days').endOf('day');
    }

    return { startDate, endDate };
};

export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

// Merges overlapping or adjacent time windows
export const mergeWindows = (windows: TimeWindow[]): TimeWindow[] => {
    // We need to make a copy in order to not mutate the original array
    const windowsCopy = windows.map((window) => ({ ...window }));
    windowsCopy.sort((a, b) => a.start.diff(b.start));
    const mergedWindows: TimeWindow[] = [];
    let currentWindowIndex = 0;

    while (currentWindowIndex < windowsCopy.length) {
        const currentWindow = windowsCopy[currentWindowIndex];
        while (
            currentWindowIndex < windows.length - 1 &&
            windowsCopy[currentWindowIndex + 1].start.isSameOrBefore(currentWindow.end)
        ) {
            currentWindow.end = moment.max(
                currentWindow.end,
                windowsCopy[currentWindowIndex + 1].end,
            );
            currentWindowIndex++;
        }
        mergedWindows.push(currentWindow);
        currentWindowIndex++;
    }

    return mergedWindows;
};

export const getTimezoneAbbreviationFromIANA = (IANAtimezone: string): string => {
    // Examples:
    // "America/Denver" -> "MST" ("MDT" in summer)
    // "America/New_York" -> "EST" ("EDT" in summer)
    // "America/Los_Angeles" -> "PST" ("PDT" in summer)
    return (
        new Intl.DateTimeFormat('en-US', {
            timeZone: IANAtimezone,
            timeZoneName: 'short',
        })
            .formatToParts(new Date())
            .find((part) => part.type === 'timeZoneName')?.value ?? IANAtimezone
    );
};
